// Safety Dashboard — admin-only crash-correlation analytics for RSA-EXISTING
// corridors. Productizes the SR-2 study: pick a state db + surveyed corridor,
// see deficiency runs vs MassDOT crash data on an OSM map, a milepost strip
// chart, descriptive stats that re-slice instantly on filter change, and the
// cached statistical verdict (GET /api/safety/verdict).
//
// Data flow: state -> collections -> (corridor P/N, analyses, crashes, verdict)
// fetched ONCE per corridor; every FilterBar control re-filters client-side via
// safetyFilters.js — filters never refetch.
import React, { useEffect, useMemo, useState } from 'react';
import * as api from './safetyApi';
import {
  DEFAULT_FILTERS, RUN_ANALYSES, applyCorrelatedOnly, clusterVisibleCrashes,
  computeDescriptiveStats, computeSiteTable, filterCrashes, filterRuns,
} from './safetyFilters';
import FilterBar from './FilterBar';
import SafetyMap from './SafetyMap';
import StripChart from './StripChart';
import SummaryStats from './SummaryStats';
import SitesTable from './SitesTable';
import CrashClustersTable from './CrashClustersTable';
import VerdictCard from './VerdictCard';
import InsightsPanel from './InsightsPanel';
import IntersectionsPanel from './IntersectionsPanel';
import './SafetyDashboard.css';

const ANALYSES = RUN_ANALYSES;           // SSD PSD GLR FSA HCA (ALL is a view, not a fetch)
const DIRECTIONS = ['P', 'N'];

// Collapsible section (same pattern as UsageDashboard's SectionCard).
const SectionCard = ({ title, meta, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-section">
      <button type="button" className="safety-section-head" onClick={() => setOpen(!open)}
        aria-expanded={open}>
        <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="safety-section-title">{title}</span>
        {meta && <span className="safety-section-meta">{meta}</span>}
      </button>
      {open && <div className="safety-section-body">{children}</div>}
    </div>
  );
};

export default function SafetyDashboard() {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState('');
  const [corridors, setCorridorList] = useState([]);
  const [corridorKey, setCorridorKey] = useState(''); // "route|pathId"
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [corridorByDir, setCorridorByDir] = useState({});
  const [analysisData, setAnalysisData] = useState({}); // {"P:SSD": payload}
  const [crashData, setCrashData] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [recomputing, setRecomputing] = useState(false);
  const [focus, setFocus] = useState(null); // row-click -> map fly-to target

  const mark = (key, on) => setLoading((l) => ({ ...l, [key]: on }));
  const fail = (key, e) => setErrors((er) => ({ ...er, [key]: e.message }));

  useEffect(() => {
    api.fetchStates()
      .then((r) => {
        setStates(r.states || []);
        if (r.states?.length === 1) setStateId(r.states[0].id);
      })
      .catch((e) => fail('states', e));
  }, []);

  useEffect(() => {
    if (!stateId) return;
    setCorridorList([]);
    setCorridorKey('');
    mark('collections', true);
    api.fetchCollections(stateId)
      .then((r) => setCorridorList(r.corridors || []))
      .catch((e) => fail('collections', e))
      .finally(() => mark('collections', false));
  }, [stateId]);

  useEffect(() => {
    if (!stateId || !corridorKey) return;
    const [route, pathId] = corridorKey.split('|');
    setCorridorByDir({});
    setAnalysisData({});
    setCrashData(null);
    setVerdict(null);
    setErrors({});
    setFilters(DEFAULT_FILTERS);

    DIRECTIONS.forEach((dir) => {
      api.fetchCorridor(stateId, route, pathId, dir)
        .then((r) => setCorridorByDir((c) => ({ ...c, [dir]: r.corridor })))
        .catch(() => setCorridorByDir((c) => ({ ...c, [dir]: null })));
      ANALYSES.forEach((analysis) => {
        api.fetchAnalysis(stateId, route, pathId, dir, analysis)
          .then((r) => setAnalysisData((a) => ({ ...a, [`${dir}:${analysis}`]: r })))
          .catch(() => setAnalysisData((a) => ({ ...a, [`${dir}:${analysis}`]: null })));
      });
    });
    mark('crashes', true);
    api.fetchCrashes(stateId, route, pathId)
      .then((r) => setCrashData(r))
      .catch((e) => fail('crashes', e))
      .finally(() => mark('crashes', false));
    mark('verdict', true);
    api.fetchVerdict(stateId, route, pathId)
      .then((r) => setVerdict(r.verdict))
      .catch((e) => fail('verdict', e))
      .finally(() => mark('verdict', false));
  }, [stateId, corridorKey]);

  const recomputeVerdict = () => {
    if (!stateId || !corridorKey) return;
    const [route, pathId] = corridorKey.split('|');
    setRecomputing(true);
    api.fetchVerdict(stateId, route, pathId, true)
      .then((r) => setVerdict(r.verdict))
      .catch((e) => fail('verdict', e))
      .finally(() => setRecomputing(false));
  };

  // ---- derived (all client-side; recompute on every filter change) ----
  // 'ALL' merges every analysis' runs (tagged so map/table can tell them apart)
  const runsByDirection = useMemo(() => {
    const wanted = filters.analysis === 'ALL' ? ANALYSES : [filters.analysis];
    const out = {};
    DIRECTIONS.forEach((dir) => {
      out[dir] = wanted.flatMap((a) =>
        (analysisData[`${dir}:${a}`]?.runs || []).map((r) => ({ ...r, analysis: a })));
    });
    return out;
  }, [analysisData, filters.analysis]);

  const analysisByDirection = useMemo(() => ({
    P: analysisData[`P:${filters.analysis}`],
    N: analysisData[`N:${filters.analysis}`],
  }), [analysisData, filters.analysis]);

  const crashDataAvailable = !!crashData
    && crashData.datasetVersion !== 'unavailable'
    && (crashData.crashes || []).length >= 0 && !errors.crashes;
  const { filteredRuns, filteredCrashes } = useMemo(() => {
    const runs = filterRuns(runsByDirection, filters);
    // analyses-only mode: crash layers/tables empty, runs untouched
    const crashes = filters.showCrashes ? filterCrashes(crashData?.crashes, filters) : [];
    if (!filters.correlatedOnly) return { filteredRuns: runs, filteredCrashes: crashes };
    const kept = applyCorrelatedOnly(runs, crashes, filters.analysis);
    return { filteredRuns: kept.runs, filteredCrashes: kept.crashes };
  }, [runsByDirection, crashData, filters]);

  // clusters recomputed from the VISIBLE crashes so they obey every filter
  // (year, severity, direction, correlated-only, ...)
  const visibleClusters = useMemo(
    () => clusterVisibleCrashes(filteredCrashes),
    [filteredCrashes]);
  const corridorExtent = useMemo(() => {
    const mins = Object.values(corridorByDir).filter(Boolean).map((c) => c.mpMin);
    const maxs = Object.values(corridorByDir).filter(Boolean).map((c) => c.mpMax);
    return mins.length ? [Math.min(...mins), Math.max(...maxs)] : null;
  }, [corridorByDir]);
  const primaryCorridor = corridorByDir.P || corridorByDir.N || null;
  const stats = useMemo(
    () => computeDescriptiveStats(filteredCrashes, filteredRuns, primaryCorridor, filters.analysis),
    [filteredCrashes, filteredRuns, primaryCorridor, filters.analysis]
  );
  const sites = useMemo(
    () => computeSiteTable(filteredRuns, filteredCrashes),
    [filteredRuns, filteredCrashes]
  );
  const criterion = filters.analysis === 'ALL'
    ? 'each analysis uses its own criterion — runs are colored by analysis on the map'
    : (analysisByDirection.P?.criterion ?? analysisByDirection.N?.criterion);

  const corridorLabel = primaryCorridor
    ? `${primaryCorridor.road || corridorKey.split('|')[0]} · MP ${primaryCorridor.mpMin}–${primaryCorridor.mpMax}`
    : '';

  return (
    <div className="safety-dashboard">
      <header className="safety-header">
        <div>
          <h1>Safety Dashboard</h1>
          <p className="safety-muted">
            RSA-EXISTING deficiencies vs. crash data{crashData ? ` · dataset ${crashData.datasetVersion}` : ''}
          </p>
        </div>
        <div className="safety-pickers">
          <label className="safety-filter">
            <span>State</span>
            <select value={stateId} onChange={(e) => setStateId(e.target.value)}>
              <option value="">Select…</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label} ({s.crashSource})
                </option>
              ))}
            </select>
          </label>
          <label className="safety-filter">
            <span>Corridor</span>
            <select value={corridorKey} onChange={(e) => setCorridorKey(e.target.value)}
              disabled={!corridors.length}>
              <option value="">
                {loading.collections ? 'Loading…' : corridors.length ? 'Select…' : 'No corridors'}
              </option>
              {corridors.map((c) => (
                <option key={`${c.route}|${c.pathId}`} value={`${c.route}|${c.pathId}`}>
                  {c.road ? `${c.road} · ` : ''}{c.route} · {c.pathId} ({Object.keys(c.directions).sort().join('/')})
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {errors.states && <div className="safety-error">Failed loading states: {errors.states}</div>}
      {errors.collections && <div className="safety-error">Failed loading corridors: {errors.collections}</div>}

      {corridorKey ? (
        <>
          <FilterBar filters={filters} setFilters={setFilters}
            crashes={crashData?.crashes} corridorExtent={corridorExtent}
            datasetYears={crashData?.datasetYears} />
          {criterion && (
            <div className="safety-note safety-note-info">
              Deficiency criterion: {criterion}
            </div>
          )}
          <SummaryStats stats={stats} analysis={filters.analysis} />

          <div className="safety-main">
            <div className="safety-map-pane">
              <SafetyMap corridors={corridorByDir} runs={filteredRuns}
                crashes={filteredCrashes} clusters={visibleClusters}
                intersections={crashData?.intersections}
                analysis={filters.analysis} focus={focus}
                showCrashes={filters.showCrashes && crashDataAvailable} />
              {loading.crashes && <div className="safety-loading-overlay">Loading crashes…</div>}
              {errors.crashes && filters.showCrashes && (
        <div className="safety-note">Crash data unavailable ({errors.crashes}) —
          showing the RDV analyses on their own.</div>
      )}
            </div>

            <div className="safety-report-pane">
              {filters.showCrashes && crashDataAvailable && (
              <SectionCard title="Insights" meta={`${filters.analysis === 'ALL' ? 'all analyses' : filters.analysis} · crash-by-crash`}>
                <InsightsPanel crashes={filteredCrashes} road={crashData?.road}
                  analysis={filters.analysis} onFocus={setFocus}
                  costTable={crashData?.costTable} />
              </SectionCard>
              )}
              {!crashDataAvailable && crashData !== null && (
                <div className="safety-note">No crash data is available for this state —
                  showing the RDV analyses on their own.</div>
              )}
              {filters.showCrashes && crashDataAvailable && (
              <SectionCard title="Statistical support" meta={corridorLabel}>
                <VerdictCard verdict={verdict} loading={loading.verdict} error={errors.verdict}
                  onRecompute={recomputeVerdict} recomputing={recomputing} />
              </SectionCard>
              )}
              <SectionCard title={`Deficiency sites (${filters.analysis})`}>
                <SitesTable sites={sites} analysis={filters.analysis} onFocus={setFocus}
                  crashes={filters.showCrashes ? filteredCrashes : []} />
              </SectionCard>
              {filters.showCrashes && crashDataAvailable && (
              <SectionCard title="Crash clusters">
                <CrashClustersTable clusters={visibleClusters} crashes={filteredCrashes}
                  runs={filteredRuns} analysis={filters.analysis} onFocus={setFocus} />
              </SectionCard>
              )}
            </div>
          </div>

          <SectionCard title="Milepost strip" meta={`${filters.analysis} vs crash locations`}>
            <StripChart analysisByDirection={analysisByDirection} crashes={filteredCrashes}
              runs={filteredRuns} filters={filters} corridorExtent={corridorExtent} />
          </SectionCard>
        </>
      ) : (
        <div className="safety-placeholder">
          Pick a state and corridor to load its analyses and crash data.
        </div>
      )}

      {stateId && (
        <SectionCard title="Intersection priorities"
          meta="district-wide, from the crash snapshot" defaultOpen={false}>
          <IntersectionsPanel stateId={stateId} onFocus={setFocus} />
        </SectionCard>
      )}
    </div>
  );
}
